import React from 'react';
import { Box, Button, Badge, useDisclosure, Collapse } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronRight,
  faPaperclip,
  faCircle,
  faCircleCheck,
  faXmarkCircle,
  faChevronUp,
  faChevronDown,
  faCircleChevronRight,
  faExclamationCircle,
  faClock,
} from '@fortawesome/pro-regular-svg-icons';
import { FingerStatus } from 'api-paths-cas';
import { Icon } from '../Icon';

// TODO: Revert this file to before it was used in Apply
export interface Props {
  status: FingerStatus;
  label: string;
  isClickable?: boolean;
  'data-selector'?: string;
  id?: string;
  onClick?: () => void;
  completeLabel?: string;
  resolveLabel?: string;
  noneLabel?: number;
  buttonLabel?: string;
  hideButton?: boolean;
  noEdit?: boolean;
  hasStart?: boolean;
  reason?: string;
  showAction?: boolean;
  actions?: React.ReactNode;
  attachmentText?: string;
  hasAttachment?: boolean;
  linkAs?: React.ElementType;
  to?: string;
}

const Finger: React.FC<Props> = (props: Props) => {
  const {
    id = 'finger',
    status,
    label,
    isClickable,
    onClick,
    completeLabel,
    resolveLabel,
    noneLabel,
    buttonLabel,
    hideButton,
    noEdit,
    hasStart,
    showAction,
    reason,
    attachmentText,
    hasAttachment = true,
    'data-selector': selector = 'finger',
    linkAs,
    to,
  } = props;

  const hasEditOrResume =
    (status !== FingerStatus.Todo && noEdit !== true) ||
    (noEdit === true && status !== FingerStatus.Complete && status !== FingerStatus.Todo);

  const isNotLocked = status !== FingerStatus.Locked;

  const isSectionStarted = hasStart && !hasEditOrResume;

  const shouldShowAction = status !== FingerStatus.Locked && showAction;

  const isActive =
    isClickable ||
    (![FingerStatus.Todo, FingerStatus.Locked].includes(status) && hideButton !== true) ||
    (isNotLocked && shouldShowAction && (hasEditOrResume || isSectionStarted));

  const { isOpen, onToggle } = useDisclosure();

  const isInteractive = isActive || reason;

  const getOnClick = () => {
    if (isActive) {
      return onClick;
    } else if (reason) {
      return onToggle;
    }
    return undefined;
  };

  const getProps = (status: FingerStatus) => {
    switch (status) {
      case FingerStatus.Todo:
        return {
          Icon: {
            icon: faCircle,
            iconColor: 'muted',
          },
          Badge: null,
        };
      case FingerStatus.InProgress:
        return {
          Icon: {
            icon: faCircleChevronRight,
            iconColor: 'orange.400',
          },
          Badge: {
            colorScheme: 'orange',
            children: buttonLabel || 'To do',
          },
        };
      case FingerStatus.Complete:
        return {
          Icon: {
            icon: faCircleCheck,
            iconColor: 'green.400',
          },
          Badge: {
            colorScheme: 'green',
            children: completeLabel || 'Done',
          },
        };
      case FingerStatus.InError:
        return {
          Icon: {
            icon: faExclamationCircle,
            iconColor: 'red.400',
          },
          Badge: {
            colorScheme: 'red',
            children: resolveLabel || 'View/Resolve',
          },
        };
      case FingerStatus.Rejected:
        return {
          Icon: {
            icon: faXmarkCircle,
            iconColor: 'red.400',
          },
          Badge: {
            colorScheme: 'red',
            children: 'Rejected',
          },
        };
      case FingerStatus.Waiting:
        return {
          Icon: {
            icon: faClock,
            iconColor: 'muted',
          },
        };
      case FingerStatus.Locked:
        return {
          Icon: {
            icon: faCircleCheck,
            iconColor: 'green.400',
          },
          Badge: {
            colorScheme: 'green',
            children: 'Locked',
          },
        };
      case FingerStatus.None:
        return {
          Icon: {
            icon: null,
            iconColor: 'muted',
          },
          Badge: noneLabel !== 0 && {
            colorScheme: 'blue',
            borderRadius: 'full',
            gap: 1,
            px: 2,
            children: (
              <>
                <Icon colorScheme="blue" icon={faPaperclip} size="sm" />
                {noneLabel}
              </>
            ),
          },
        };

      default:
        return {
          Icon: {
            icon: faCircle,
            iconColor: 'muted',
          },
          Badge: null,
        };
    }
  };

  return (
    <Box data-selector={`${id}-div`} borderBottomWidth="2px">
      <Button
        {...(linkAs && to ? { as: linkAs, to } : {})}
        onClick={getOnClick()}
        data-selector={selector}
        size="lg"
        variant="ghost"
        colorScheme={isActive ? 'blue' : 'gray'}
        isDisabled={!isInteractive}
        borderRadius="none"
        textAlign="left"
        alignItems="start"
        gap={{ base: 1, lg: 2 }}
        px={{ base: 0, lg: 4 }}
        py={5}
        height="auto"
        width="100%"
        _disabled={{ opacity: 1, cursor: 'not-allowed' }}
      >
        {getProps(status).Icon.icon && (
          <Box
            as="span"
            display="block"
            color={getProps(status).Icon.iconColor}
            width={6}
            lineHeight={6}
            flexShrink="0"
            mr={{ base: 1, lg: 2 }}
          >
            <FontAwesomeIcon icon={getProps(status).Icon.icon!} size="lg" />
          </Box>
        )}

        <Box as="span" display="block" fontSize="lg" lineHeight="6" flexGrow="1" whiteSpace="normal">
          <Box fontWeight="medium">
            {status === FingerStatus.Rejected ? (
              <Box as="del" color="muted">
                {label}
              </Box>
            ) : (
              label
            )}

            {Boolean(attachmentText?.length) && (
              <Box fontSize={'xs'} mt={2} lineHeight={4} color="muted">
                {hasAttachment && <FontAwesomeIcon icon={faPaperclip} fixedWidth className="utility-icon" />}
                {attachmentText}
              </Box>
            )}
          </Box>

          {reason ? (
            <Collapse in={isOpen} animateOpacity>
              <Box as="blockquote" borderLeftWidth="4px" px={3} mt={3}>
                <Box fontWeight="medium" color="muted" mb={2}>
                  Reason for document rejection:
                </Box>

                {reason}
              </Box>
            </Collapse>
          ) : null}
        </Box>

        {getProps(status).Badge ? (
          <Box as="span" display="block">
            <Badge {...getProps(status).Badge} verticalAlign={'top'} />
          </Box>
        ) : null}

        {isActive ? (
          <Box width={6} lineHeight={6}>
            <FontAwesomeIcon icon={faChevronRight} fixedWidth className="utility-icon" />
          </Box>
        ) : null}

        {reason ? (
          <Box width={6} lineHeight={6}>
            <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} fixedWidth className="utility-icon" />
          </Box>
        ) : null}

        {getOnClick() === undefined ? <Box width={6} /> : null}
      </Button>
    </Box>
  );
};

export default Finger;
